import { useMutation, useQuery } from "@tanstack/react-query";
import { queryClient } from "@/app/query-client";
import { useMemberSession } from "@/entities/member";
import {
  confirmNotificationPopup,
  getNotification,
  getNotificationUnreadCount,
  getPendingNotificationPopup,
  getRecentNotifications,
  listNotifications,
  markAllNotificationsRead,
  markNotificationRead,
  trackNotificationClick,
  type ListNotificationsParams,
} from "../api/notification-api";

const LIST_POLL_INTERVAL = 30_000;
const RECENT_POLL_INTERVAL = 60_000;

function pollWhileVisible(interval: number) {
  return typeof document !== "undefined" &&
    document.visibilityState === "visible"
    ? interval
    : false;
}

export const notificationKeys = {
  all: ["member-notifications"] as const,
  lists: () => [...notificationKeys.all, "list"] as const,
  list: (params: ListNotificationsParams) =>
    [...notificationKeys.lists(), params] as const,
  details: () => [...notificationKeys.all, "detail"] as const,
  detail: (id: number) => [...notificationKeys.details(), id] as const,
  recent: (limit: number) =>
    [...notificationKeys.all, "recent", limit] as const,
  unreadCount: () => [...notificationKeys.all, "unread-count"] as const,
  popup: (loginVersion: number) =>
    [...notificationKeys.all, "popup", loginVersion] as const,
};

async function refreshNotifications() {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: notificationKeys.lists() }),
    queryClient.invalidateQueries({ queryKey: notificationKeys.details() }),
    queryClient.invalidateQueries({ queryKey: notificationKeys.unreadCount() }),
    queryClient.invalidateQueries({
      queryKey: [...notificationKeys.all, "recent"],
    }),
  ]);
}

export function useNotifications(
  params: ListNotificationsParams,
  active = true,
) {
  const authenticated = useMemberSession((state) => state.authenticated);
  return useQuery({
    queryKey: notificationKeys.list(params),
    queryFn: async () => (await listNotifications(params)).data,
    enabled: authenticated && active,
    staleTime: 10_000,
    refetchInterval: () =>
      active ? pollWhileVisible(LIST_POLL_INTERVAL) : false,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });
}

export function useNotificationDetail(id?: number, active = true) {
  const authenticated = useMemberSession((state) => state.authenticated);
  return useQuery({
    queryKey: notificationKeys.detail(id ?? 0),
    queryFn: async () => (await getNotification(id!)).data,
    enabled: authenticated && active && id !== undefined,
    staleTime: 10_000,
    refetchOnWindowFocus: true,
  });
}

export function useRecentNotifications(limit = 5) {
  const authenticated = useMemberSession((state) => state.authenticated);
  return useQuery({
    queryKey: notificationKeys.recent(limit),
    queryFn: async () => (await getRecentNotifications(limit)).data,
    enabled: authenticated,
    staleTime: 30_000,
    refetchInterval: () => pollWhileVisible(RECENT_POLL_INTERVAL),
    refetchOnWindowFocus: true,
  });
}

export function useNotificationUnreadCount() {
  const authenticated = useMemberSession((state) => state.authenticated);
  return useQuery({
    queryKey: notificationKeys.unreadCount(),
    queryFn: async () => (await getNotificationUnreadCount()).data,
    enabled: authenticated,
    staleTime: 15_000,
    refetchInterval: () => pollWhileVisible(LIST_POLL_INTERVAL),
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });
}

export function useNotificationPopup(loginVersion: number) {
  const authenticated = useMemberSession((state) => state.authenticated);
  return useQuery({
    queryKey: notificationKeys.popup(loginVersion),
    queryFn: async () => (await getPendingNotificationPopup()).data,
    enabled: authenticated,
    staleTime: 0,
    refetchInterval: () => pollWhileVisible(LIST_POLL_INTERVAL),
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    retry: false,
  });
}

export function useMarkNotificationRead() {
  return useMutation({
    mutationFn: markNotificationRead,
    onSuccess: refreshNotifications,
  });
}

export function useMarkAllNotificationsRead() {
  return useMutation({
    mutationFn: markAllNotificationsRead,
    onSuccess: refreshNotifications,
  });
}

export function useConfirmNotificationPopup() {
  return useMutation({
    mutationFn: confirmNotificationPopup,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [...notificationKeys.all, "popup"],
      });
    },
    onSettled: refreshNotifications,
  });
}

export function useTrackNotificationClick() {
  return useMutation({
    mutationFn: trackNotificationClick,
    onSuccess: refreshNotifications,
  });
}

export type { ListNotificationsParams };
